import React, { useEffect, useState } from "react";
import { Button, Card, Dropdown, Segment } from "semantic-ui-react";
import { getJobTitleAndCompanyHeading } from "../../../js/helpers/getJobTitleAndCompanyHeading";
import previewHoverIcon from "../../../assets/icons/preview-hover-icon.svg";
import { openModal } from "../../../app/common/modals/modalReducer";
import { JOB_DETAILS_MODAL } from "../../../app/common/modals/ModalManager";
import { useDispatch, useSelector } from "react-redux";
import sortBy from "lodash/sortBy";
import useFirestoreCollection from "../../../app/hooks/useFirestoreCollection";
import {
  listenToJobApplications,
  logErrorInFirestore,
} from "../../../app/firestore/firestoreService";
import {
  LOG_ERROR_CODES,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import ReviewApplication from "./ReviewApplication";
import BackButton from "../../../app/common/components/BackButton";
import {
  callAPI,
  CANDIDATE_SUGGESTIONS_ENDPOINT,
} from "../../../app/api/callAPI";
import ProfileSuggestionCard from "./ProfileSuggestionCard";
import cuid from "cuid";
import smartSearch from "../../../assets/icons/smart-search.svg";
import InfoPopup from "../../../app/common/components/InfoPopup";

const DESC = "desc";
const ASC = "asc";

export default function ApplicantsPane({ handleBackToManage, job }) {
  const dispatch = useDispatch();

  const { currentUserAccount } = useSelector((state) => state.account);
  const { loading } = useSelector((state) => state.async);
  const { responsive } = useSelector((state) => state.app);

  const [applications, setApplications] = useState(null);
  const [orderBy, setOrderBy] = useState(DESC);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [profileSuggestions, setProfileSuggestions] = useState([]);
  const [applicantRecommendations, setApplicantRecommendations] = useState([]);

  useFirestoreCollection({
    shouldExecute: currentUserAccount.isAdmin === true,
    query: () =>
      listenToJobApplications({
        user: "employerId",
        userId: job.employerId,
        jobId: job.id,
      }),
    data: (results) => setApplications(results),
    deps: [dispatch],
    queryCode: QUERY_CODES.listenToJobApplications,
  });

  useEffect(() => {
    if (job.isClosed || !applications) return;
    setIsLoadingSuggestions(true);

    const applied = applications.map((application) => ({
      jobSeekerId: application.jobSeekerId,
      ref: application.candidateProfileRef,
      id: application.candidateProfileId,
    }));

    callAPI({
      endpoint: CANDIDATE_SUGGESTIONS_ENDPOINT,
      opts: {
        body: {
          job,
          applied,
          isAdmin: currentUserAccount.isAdmin,
        },
      },
    })
      .then(({ profileSuggestions, applicantRecommendations }) => {
        setProfileSuggestions(profileSuggestions);
        setApplicantRecommendations(applicantRecommendations);
      })
      .catch((e) => {
        setProfileSuggestions([]);
        setApplicantRecommendations([]);
        logErrorInFirestore({
          error: `Could not fetch candidate suggestions ${job.id}`,
          errorCode: LOG_ERROR_CODES.profileSuggestions,
          notes: { errorStack: e.toString() },
        });
      })
      .finally(() => setIsLoadingSuggestions(false));
  }, [job, applications, currentUserAccount.isAdmin]);

  const options = [
    { key: "e", text: "Newest first", value: DESC },
    { key: "d", text: "Oldest first", value: ASC },
  ];

  let sortedApplicants = [];
  if (orderBy === DESC) {
    sortedApplicants = sortBy(applications || [], "dateApplied").reverse();
  } else if (orderBy === ASC) {
    sortedApplicants = sortBy(applications || [], "dateApplied");
  }

  return (
    <>
      <BackButton action={handleBackToManage} />
      <h2>
        Review Applications {`(${applications ? applications.length : 0})`}
      </h2>
      <div>
        <span>
          {getJobTitleAndCompanyHeading({
            title: job.title,
            company: job.company,
          })}
        </span>
        <span>
          <Button
            className={"overwrite-button icon-text-button"}
            content={<span className={"action-text"}>View</span>}
            icon={
              <img
                src={previewHoverIcon}
                className={"icon-text-button-img"}
                style={{ width: 20, height: 15.3 }}
                alt="edit"
              />
            }
            onClick={() => {
              dispatch(
                openModal({
                  modalType: JOB_DETAILS_MODAL,
                  modalProps: { job },
                })
              );
            }}
            style={{ margin: "0 0 0 20px" }}
          />
        </span>
      </div>
      <div className="width-90">
        <div className={"row-bar"}>
          <div>
            Order by:{" "}
            <Dropdown
              floating
              inline
              options={options}
              defaultValue={orderBy}
              onChange={(e, { value }) => setOrderBy(value)}
            />
          </div>
        </div>
        <Segment
          basic
          className="semantic-div"
          style={{ paddingLeft: 15 }}
          loading={loading}
        >
          {sortedApplicants.length > 0 &&
            sortedApplicants.map((application) => (
              <ReviewApplication
                application={application}
                key={application.id}
                isAdmin={currentUserAccount.isAdmin}
              />
            ))}
        </Segment>
        <Segment
          basic
          className="semantic-div"
          style={{ padding: "20px 15px", marginTop: 30 }}
        >
          <h3>
            Profile suggestions{" "}
            <span className="h4-text tag-text" style={{ marginLeft: 12 }}>
              <img
                className={"next-to-text"}
                src={smartSearch}
                style={{ width: 18, height: 18 }}
                alt="smart search"
              />{" "}
              Smart search
              <InfoPopup
                triggerSpacing={"0 5px"}
                text={
                  "Using our machine learning tool, we'll analyse your job details and search our database to give you relevant profile suggestions."
                }
              />
            </span>
          </h3>
          {isLoadingSuggestions && (
            <h4 className="italic-text">Searching for profiles…</h4>
          )}
          {profileSuggestions.length > 0 && (
            <Card.Group
              style={{
                marginLeft: responsive.isMobile ? 12 : 0,
                margin: "5px 0 25px",
              }}
            >
              {profileSuggestions.map((profile) => (
                <ProfileSuggestionCard
                  key={cuid()}
                  profile={profile}
                  style={{
                    float: "none",
                    margin: "16px 20px 0 0",
                    width: 320,
                  }}
                />
              ))}
            </Card.Group>
          )}
          {job.isClosed && (
            <div className="h4-text" style={{ margin: "5px 10px" }}>
              Suggestions not available for expired jobs
            </div>
          )}
          {!job.isClosed &&
            !isLoadingSuggestions &&
            profileSuggestions.length === 0 &&
            applicantRecommendations.length === 0 && (
              <div className="h4-text" style={{ margin: "5px 10px" }}>
                We couldn’t find relevant suggestions for you at this time.
                Check back later!
              </div>
            )}
          {applicantRecommendations.length > 0 && (
            <>
              <div
                className="h4-text tag-text"
                style={{ margin: "35px 0 0 12px" }}
              >
                <img
                  className={"next-to-text"}
                  src={smartSearch}
                  style={{ width: 18, height: 18 }}
                  alt="smart search"
                />{" "}
                We found similar profiles to your applicants
              </div>
              {applicantRecommendations.length > 0 && (
                <Card.Group
                  style={{
                    marginLeft: responsive.isMobile ? 12 : 0,
                    margin: "5px 0 15px",
                  }}
                >
                  {applicantRecommendations.map((profile) => (
                    <ProfileSuggestionCard
                      key={cuid()}
                      profile={profile}
                      style={{
                        float: "none",
                        margin: "16px 20px 0 0",
                        width: 320,
                      }}
                    />
                  ))}
                </Card.Group>
              )}
            </>
          )}
        </Segment>
      </div>
    </>
  );
}
